import React, { useEffect, useState } from 'react';
import { post, put } from "../../utils/fetchAPI";
import { toast } from "react-toastify";
import { FaTimes } from 'react-icons/fa';
import MedicineOptionSelect from "./medicine-option-select";

const emptyMedicine = {
    name: '',
    generic: '',
    type: '',
    power: '',
    manufacturer: '',
};

const MedicineForm = ({ medicine, genericOptions = [], typeOptions = [], manufacturerOptions = [], onSuccess, onClose }) => {
    const [medicineData, setMedicineData] = useState(emptyMedicine);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (medicine) {
            setMedicineData({
                name: medicine.name || '',
                generic: medicine.generic || '',
                type: medicine.type || '',
                power: medicine.power || '',
                manufacturer: medicine.manufacturer || '',
            });
        } else {
            setMedicineData(emptyMedicine);
        }
    }, [medicine]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setMedicineData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name) => (value) => {
        setMedicineData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const dataToSend = {
            name: medicineData.name.trim(),
            generic: medicineData.generic.trim(),
            type: medicineData.type.trim(),
            power: medicineData.power.trim(),
            manufacturer: medicineData.manufacturer.trim(),
        };

        const requiredFields = [
            ['name', 'Medicine name is required!'],
            ['generic', 'Generic name is required!'],
            ['type', 'Dosage form is required!'],
        ];
        const missing = requiredFields.find(([field]) => !dataToSend[field]);
        if (missing) {
            toast.error(missing[1], {
                position: "top-center",
                theme: "light",
            });
            return;
        }

        setIsSaving(true);
        try {
            if (medicine) {
                await put(`/medicine/${medicine._id}`, dataToSend);
                toast.success('Medicine updated successfully!', {
                    position: "top-center",
                    theme: "light",
                    hideProgressBar: true,
                });
            } else {
                await post('/medicine/create', dataToSend);
                toast.success('Medicine added to the global list!', {
                    position: "top-center",
                    theme: "light",
                    hideProgressBar: true,
                });
            }
            onSuccess(!medicine);
            onClose();
        } catch (error) {
            console.error('Error saving medicine:', error);
            const errorMessage = error.response?.data?.errors?.[0]?.msg ||
                error.response?.data?.message ||
                'Failed to save medicine. Please try again.';
            toast.error(errorMessage, {
                position: "top-center",
                theme: "light",
                hideProgressBar: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className='py-6 min-[320px]:py-2 lg:px-5 min-[320px]:px-2'>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-blue-500 lg:text-3xl sm:text-lg font-bold">
                    {medicine ? "Edit Medicine" : "Create New Medicine"}
                </h2>
                <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700"
                >
                    <FaTimes className="w-6 h-6" />
                </button>
            </div>

            <p className="text-xs text-gray-600 bg-teal-50 border border-teal-100 rounded-md p-2 mb-4">
                {medicine
                    ? "This medicine is in the global list. Changes apply to every doctor's medicine search; prescriptions already written keep the old values."
                    : "This medicine is added to the global list, so every doctor in every organization can find it in their medicine search."}
            </p>

            <form className='text-black lg:text-sm min-[320px]:text-xs flex flex-col w-full' onSubmit={handleSubmit}>
                {/* Name */}
                <div className="mb-4">
                    <label className="block mb-2 font-medium">Medicine Name (Brand) *</label>
                    <input
                        type="text"
                        name="name"
                        value={medicineData.name}
                        onChange={handleInputChange}
                        placeholder="e.g., Napa"
                        className="w-full text-sm border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        required
                    />
                </div>

                {/* Generic */}
                <div className="mb-4">
                    <label htmlFor="medicine-generic" className="block mb-2 font-medium">Generic Name *</label>
                    <MedicineOptionSelect
                        inputId="medicine-generic"
                        value={medicineData.generic}
                        options={genericOptions}
                        onChange={handleSelectChange('generic')}
                        placeholder="Select or type a generic name..."
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Dosage form */}
                    <div>
                        <label htmlFor="medicine-type" className="block mb-2 font-medium">Dosage Form *</label>
                        <MedicineOptionSelect
                            inputId="medicine-type"
                            value={medicineData.type}
                            options={typeOptions}
                            onChange={handleSelectChange('type')}
                            placeholder="Select or type..."
                        />
                    </div>

                    {/* Strength */}
                    <div>
                        <label className="block mb-2 font-medium">Strength</label>
                        <input
                            type="text"
                            name="power"
                            value={medicineData.power}
                            onChange={handleInputChange}
                            placeholder="e.g., 500 mg"
                            className="w-full text-sm border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>
                </div>

                {/* Manufacturer */}
                <div className="mb-4">
                    <label htmlFor="medicine-manufacturer" className="block mb-2 font-medium">Manufacturer</label>
                    <MedicineOptionSelect
                        inputId="medicine-manufacturer"
                        value={medicineData.manufacturer}
                        options={manufacturerOptions}
                        onChange={handleSelectChange('manufacturer')}
                        placeholder="Select or type a manufacturer..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Generic, dosage form and manufacturer list the values already in use — pick one
                        where it matches, since search ranking and doctors' preferred-manufacturer sorting
                        depend on the exact spelling. If it is not listed, type the new value.
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 mt-6 pt-4 border-t">
                    <button
                        type="submit"
                        disabled={isSaving}
                        className="text-sm px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-60"
                    >
                        {isSaving ? 'Saving...' : medicine ? 'Update' : 'Create'}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-sm px-6 py-2 bg-gray-300 text-black rounded-md hover:bg-gray-400 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default MedicineForm;
